/* eslint-disable no-unused-vars */

import React from 'react';
import { produce } from 'immer';
import { setImmutableNestedState } from 'common/components/CopyMagic/contexts/helpers/setImmutableNestedState';
import type { MessageDataType } from 'common/components/CopyMagic/CopyMagic.types';
import {
  useLocalStorageMessageCache,
  resetLocalStorageMessageCache,
} from 'common/components/CopyMagic/hooks/useLocalStorageMessageCache';
import useChatSessionId from 'common/components/CopyMagic/hooks/useChatSessionId';
import type { MessagesContextInterface, MessagesType } from './MessagesContext.types';

/*
MessagesContext
  Stores chat completion message data
  Receives an array of `MessageDataType`s and creates a Map of `MessageType`
*/
const noop = () => {};
const DEFAULTS: MessagesContextInterface = {
  messages: produce(new Map(), () => {}),
  setNewMessages: noop,
  replaceMessages: noop,
  resetMessages: noop,
  getMessageDatas: () => [],
  findMessage: () => [],
  findMessagesBy: () => [],
  findLastMessageBy: () => [],
  findLastMessage: () => [],
};

const messageKey = (time: number, index: number, messageData: MessageDataType) =>
  `${time}_${index}_${Math.floor(Math.random() * 1000)}_${messageData.role}`;

export const MessagesContext = React.createContext<MessagesContextInterface>(DEFAULTS);

export const useMessagesContext = () => {
  const context = React.useContext(MessagesContext);
  if (!context) throw new Error(`outside of provider`);
  return context;
};

export const MessagesProvider = ({ children }: { children: React.ReactElement }) => {
  const [providerState, setProviderState] = React.useState<MessagesContextInterface>(DEFAULTS);
  const { setNewChatSessionId } = useChatSessionId();

  const providerValue: MessagesContextInterface = {
    ...providerState,

    // Set new `messages` Map values and optionally `clearMessages`
    setNewMessages: (newMessageDatas, { clearMessages = false } = {}) => {
      setImmutableNestedState<MessagesContextInterface, MessagesType>(
        setProviderState,
        'messages',
        (draft) => {
          if (clearMessages) draft.clear();
          const createdAt = Date.now();
          newMessageDatas.forEach((data, index) => {
            draft.set(messageKey(createdAt, index, data), {
              data: {
                role: data.role,
                content: data.content,
              },
              createdAt,
              contentId: data.contentId,
            });
          });
        },
      );
    },

    replaceMessages: (messages) => {
      setImmutableNestedState<MessagesContextInterface, MessagesType>(
        setProviderState,
        'messages',
        (draft) => {
          draft.clear();
          Array.from(messages.entries()).forEach((entry) => draft.set(...entry));
        },
      );
    },

    resetMessages: () => {
      setImmutableNestedState<MessagesContextInterface, MessagesType>(
        setProviderState,
        'messages',
        (draft) => {
          draft.clear();
        },
      );
      resetLocalStorageMessageCache();
      setNewChatSessionId({ restarted: true });
    },

    getMessageDatas: (count) => {
      let messageValues = Array.from(providerValue.messages.values());
      // Get last X number of messages
      if (count) messageValues = messageValues.slice(-count);
      return messageValues.map((item) => item.data);
    },

    findMessage: (key) => {
      const message = providerValue.messages.get(key);
      if (message) return [key, message];
      return [];
    },

    findMessagesBy: ({ role }) => {
      const messagesEntries = Array.from(providerValue.messages).filter(
        ([_key, item]) => item.data.role === role,
      );
      return messagesEntries;
    },

    findLastMessageBy: ({ role }) => {
      const messageEntry = providerValue.findMessagesBy({ role }).pop();
      return messageEntry || [];
    },

    findLastMessage: () => Array.from(providerValue.messages).pop() || [],
  };

  useLocalStorageMessageCache({
    messages: providerValue.messages,
    replaceMessages: providerValue.replaceMessages,
  });

  return <MessagesContext.Provider value={providerValue}>{children}</MessagesContext.Provider>;
};
